import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { timeCustomersAPI, timeLeadsAPI } from '../services/api';
import { CreateTimeLead, TimeCustomerLookup, TimeLead, TimeLeadTag, UpdateTimeLead } from '../types';
import LeadActivities from './LeadActivities';
import LeadForm from './LeadForm';

// Separate new/edit lead page, mirroring how Flow keeps create/edit
// project forms on their own routes instead of inline on the list.
const LeadEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [lead, setLead] = useState<TimeLead | null>(null);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [tags, setTags] = useState<TimeLeadTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [full, customerList, tagList] = await Promise.all([
        isEditMode ? timeLeadsAPI.getLead(Number(id)) : Promise.resolve(null),
        timeCustomersAPI.getCustomers(undefined, true),
        timeLeadsAPI.getTags(),
      ]);
      setLead(full);
      setCustomers(customerList ?? []);
      setTags(tagList ?? []);
    } catch (error) {
      console.error('[FIX:leads] Failed to load lead for editing', error);
      setLoadError(isEditMode ? 'Lead could not be loaded.' : 'Lead form could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = () => {
    navigate('/leads');
  };

  const handleSubmit = async (payload: CreateTimeLead) => {
    setSaving(true);
    setSaveError('');
    try {
      if (lead) {
        const update: UpdateTimeLead = { ...payload, id: lead.id };
        await timeLeadsAPI.updateLead(lead.id, update);
        toast.success('Lead updated');
      } else {
        await timeLeadsAPI.createLead(payload);
        toast.success('Lead created');
      }
      navigate('/leads');
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Lead could not be saved.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border" role="status"></div>
          <p className="text-muted">Loading lead...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fade-in">
        <div className="page-header">
          <div>
            <h1>{isEditMode ? 'Edit lead' : 'New lead'}</h1>
          </div>
        </div>
        <div role="alert" className="card">
          <p>{loadError}</p>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={load}>
              Retry
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Back to leads
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <LeadForm
        lead={lead}
        customers={customers}
        tags={tags}
        onCustomersChange={setCustomers}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        saving={saving}
        saveError={saveError}
      />
      {lead && <LeadActivities leadId={lead.id} />}
    </div>
  );
};

export default LeadEditPage;
